import { Text, View, useColorScheme, Platform, TouchableOpacity } from 'react-native';
import React, { Suspense, memo, lazy, useState } from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import MenuIconCard from '@components/Global/MenuIconCard';
import { useTranslation } from 'react-i18next';
import { XStack } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';

const AnimatedLottieView = lazy(() => import('@utils/lottieAnimation'));

const LazyLottieView = (props) => (
  <Suspense fallback={<View />}>
    <AnimatedLottieView {...props} />
  </Suspense>
);

const FlowMenu = ({ navigation }) => {
  const theme = themeSelector();
  const { t } = useTranslation();
  const [headerOpen, setHeaderOpen] = useState(true);

  // Mock flow stats - replace with actual data when available
  const flowStats = {
    totalFlows: 0,
    activeFlows: 0,
    manualFlows: 0,
    aiCallFlows: 0,
    salesFlows: 0,
  };

  const activeCategories = Object.values(flowStats).filter(count => count > 0).length;

  return (
    <DefaultScreen backButton style={{ backgroundColor: theme ? "white" : "black" }} >
      {/* Header with Flow Insights */}
      <Animatable.View
        animation="fadeInDown"
        duration={800}
        style={{
          alignSelf: 'stretch',
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 20,
          padding: 24,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: theme ? 0.1 : 0.3,
          shadowRadius: 20,
          elevation: 10,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', alignSelf: 'stretch' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <View style={{
              backgroundColor: theme ? "#f0fdf4" : "#064e3b",
              borderRadius: 12,
              padding: 12,
              marginRight: 16
            }}>
              <MaterialIcons
                name="account-tree"
                size={24}
                color={theme ? "#059669" : "#10b981"}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{
                color: theme ? "#1e293b" : "#f1f5f9",
                fontWeight: '700',
                fontSize: 18,
                marginBottom: 4
              }}>
                {t('Flow Library')}
              </Text>
              <Text style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                {t('Manage and automate your business workflows')}
              </Text>
            </View>
          </View>

          <TouchableOpacity
            onPress={() => setHeaderOpen(!headerOpen)}
            style={{ padding: 8 }}
          >
            <MaterialIcons
              name={headerOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
              size={28}
              color={theme ? '#64748b' : '#94a3b8'}
            />
          </TouchableOpacity>
        </View>

        {headerOpen && (
          <Animatable.View
            animation="fadeInDown"
            duration={400}
          >
            {/* Stats Grid */}
            <XStack gap={12} flexWrap="wrap" justifyContent="space-between" style={{ marginTop: 20 }}>
              {[
                {
                  title: t('Total Flows'),
                  value: flowStats.totalFlows.toString(),
                  icon: 'account-tree',
                  color: theme ? "#059669" : "#10b981",
                  bgColor: theme ? "#d1fae5" : "#064e3b"
                },
                {
                  title: t('Active Flows'),
                  value: flowStats.activeFlows.toString(),
                  icon: 'play-circle-filled',
                  color: theme ? "#0ea5e9" : "#38bdf8",
                  bgColor: theme ? "#dbeafe" : "#0c4a6e"
                },
                {
                  title: t('Flow Categories'),
                  value: '3',
                  icon: 'category',
                  color: theme ? "#7c3aed" : "#a78bfa",
                  bgColor: theme ? "#ede9fe" : "#312e81"
                },
                {
                  title: t('Manual Flows'),
                  value: flowStats.manualFlows.toString(),
                  icon: 'touch-app',
                  color: theme ? "#f59e0b" : "#fbbf24",
                  bgColor: theme ? "#fef3c7" : "#451a03"
                },
                {
                  title: t('AI Call Flows'),
                  value: flowStats.aiCallFlows.toString(),
                  icon: 'phone',
                  color: theme ? "#ef4444" : "#f87171",
                  bgColor: theme ? "#fee2e2" : "#7f1d1d"
                },
                {
                  title: t('Sales Flows'),
                  value: flowStats.salesFlows.toString(),
                  icon: 'trending-up',
                  color: theme ? "#8b5cf6" : "#a78bfa",
                  bgColor: theme ? "#f3e8ff" : "#3c1361"
                }
              ].map((stat, index) => (
                <Animatable.View
                  key={index}
                  animation="bounceIn"
                  delay={index * 100}
                  style={{
                    flex: 1,
                    minWidth: 140,
                    backgroundColor: stat.bgColor,
                    borderRadius: 16,
                    padding: 16,
                    alignItems: 'center',
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                  }}
                >
                  <MaterialIcons
                    name={stat.icon as any}
                    size={20}
                    color={stat.color}
                    style={{ marginBottom: 6 }}
                  />
                  <Text style={{
                    fontSize: 18,
                    fontWeight: '700',
                    color: stat.color,
                    marginBottom: 2
                  }}>
                    {stat.value}
                  </Text>
                  <Text style={{
                    fontSize: 11,
                    fontWeight: '600',
                    color: theme ? "#64748b" : "#94a3b8",
                    textAlign: 'center'
                  }}>
                    {stat.title}
                  </Text>
                </Animatable.View>
              ))}
            </XStack>
          </Animatable.View>
        )}
      </Animatable.View>
      <View style={{ alignSelf: "stretch", paddingHorizontal: Platform.select({ native: "5%" }) }}>
        <XStack rowGap={Platform.select({ web: 60, default: 20 })} columnGap={Platform.select({ web: 60, default: 20 })} flexWrap="wrap" style={{ paddingVertical: 25, gap: 20, paddingBottom: 150, alignSelf: 'stretch', flexDirection: 'row', flexWrap: 'wrap' }}>
          <MenuIconCard pressFunction={() => alert("Coming soon!")} subtitle={`${t('Initiate flows on request')}`} cardTitle={`${t('Manual Flows')}`}>
            <LazyLottieView source={require('@assets/animations/111843-winner.json')} loop={false} style={{ width: '100%', height: 70, alignSelf: 'center', justifyContent: 'center' }} autoPlay />
          </MenuIconCard>
          <MenuIconCard pressFunction={() => navigation.navigate('CallFlows')} subtitle={`${t('Design AI call flows')}`} cardTitle={`${t('AI Call Flows')}`}>
            <LazyLottieView source={require('@assets/animations/85569-phone-call.json')} loop={false} style={{ width: '100%', height: 70, alignSelf: 'center', justifyContent: 'center' }} autoPlay />
          </MenuIconCard>
          <MenuIconCard pressFunction={() => navigation.navigate('CallFlows')} subtitle={`${t('Assign and create sales flows')}`} cardTitle={`${t('Sales Flows')}`}>
            <LazyLottieView source={require('@assets/animations/40272-business-app-animation.json')} loop={false} style={{ width: '100%', height: 70, alignSelf: 'center', justifyContent: 'center' }} autoPlay />
          </MenuIconCard>
        </XStack>
      </View>
    </DefaultScreen >
  )
}
export default memo(FlowMenu);